import { type Unsubscribe } from '../utils/Unsubscribe';
import {
  createBrowserFrameScheduler,
  createDocumentVisibilitySource,
  type FrameScheduler,
  type VisibilitySource,
} from './FrameScheduler';

/**
 * Longest step the loop will ever report, in seconds.
 *
 * Without a ceiling, a tab that was hidden for a minute resumes with a single enormous step and every
 * moving thing teleports through every wall. Twenty frames per second is slow enough to be visible and
 * fast enough to keep collisions honest.
 */
export const MAX_DELTA_SECONDS = 0.05;

const MILLISECONDS_PER_SECOND = 1000;

/** Construction options for {@link GameLoop}. */
export interface GameLoopOptions {
  /** Called once per frame with the elapsed seconds since the previous frame. */
  readonly onTick: (deltaSeconds: number) => void;
  /** Overrides {@link MAX_DELTA_SECONDS}. */
  readonly maxDeltaSeconds?: number | undefined;
  /** Overrides the frame source; defaults to `requestAnimationFrame`. */
  readonly scheduler?: FrameScheduler | undefined;
  /** Overrides the visibility source; defaults to the document. */
  readonly visibility?: VisibilitySource | undefined;
}

/**
 * Drives one callback per display refresh, with a clamped delta and automatic pausing.
 *
 * While the page is hidden the loop stops requesting frames entirely rather than ticking into a
 * backgrounded canvas, and the frame that resumes it reports a zero delta so nothing jumps.
 *
 * The loop knows only about time. What happens inside a frame is the engine's business.
 */
export class GameLoop {
  readonly #onTick: (deltaSeconds: number) => void;
  readonly #maxDeltaSeconds: number;
  readonly #scheduler: FrameScheduler;
  readonly #visibility: VisibilitySource;
  readonly #unsubscribeVisibility: Unsubscribe;

  #frameHandle: number | undefined;
  #previousTimestampMs: number | undefined;
  #running = false;
  #disposed = false;

  constructor(options: GameLoopOptions) {
    this.#onTick = options.onTick;
    this.#maxDeltaSeconds = options.maxDeltaSeconds ?? MAX_DELTA_SECONDS;
    this.#scheduler = options.scheduler ?? createBrowserFrameScheduler();
    this.#visibility = options.visibility ?? createDocumentVisibilitySource();
    this.#unsubscribeVisibility = this.#visibility.onVisibilityChange(this.#onVisibilityChange);
  }

  /** Whether the loop has been started and not yet stopped, even while paused by a hidden page. */
  get isRunning(): boolean {
    return this.#running;
  }

  /** Whether a frame is currently scheduled. False while the page is hidden. */
  get isTicking(): boolean {
    return this.#frameHandle !== undefined;
  }

  /** Starts ticking. Does nothing when already running. */
  start(): void {
    if (this.#running || this.#disposed) {
      return;
    }
    this.#running = true;
    this.#previousTimestampMs = undefined;
    this.#schedule();
  }

  /** Stops ticking and cancels any pending frame. */
  stop(): void {
    this.#running = false;
    this.#cancel();
  }

  /** Stops the loop and releases its visibility subscription. */
  dispose(): void {
    if (this.#disposed) {
      return;
    }
    this.#disposed = true;
    this.stop();
    this.#unsubscribeVisibility();
  }

  #schedule(): void {
    if (!this.#running || this.#frameHandle !== undefined || !this.#visibility.isVisible) {
      return;
    }
    this.#frameHandle = this.#scheduler.request(this.#tick);
  }

  #cancel(): void {
    if (this.#frameHandle === undefined) {
      return;
    }
    this.#scheduler.cancel(this.#frameHandle);
    this.#frameHandle = undefined;
  }

  /*
   * The next frame is requested only after onTick returns, so a throwing frame halts the loop instead
   * of scheduling itself forever and drowning the console.
   */
  readonly #tick = (timestampMs: number): void => {
    this.#frameHandle = undefined;
    const previous = this.#previousTimestampMs;
    this.#previousTimestampMs = timestampMs;

    const elapsed = previous === undefined ? 0 : (timestampMs - previous) / MILLISECONDS_PER_SECOND;
    const deltaSeconds = Math.min(Math.max(0, elapsed), this.#maxDeltaSeconds);

    this.#onTick(deltaSeconds);
    this.#schedule();
  };

  readonly #onVisibilityChange = (): void => {
    if (this.#visibility.isVisible) {
      this.#previousTimestampMs = undefined;
      this.#schedule();
      return;
    }
    this.#cancel();
  };
}
